import { useState } from 'react'
import { downloadXLSX } from '@/lib/download-file'
import { captureException } from '@/lib/errors'
import type { TagNameLookup } from '@/lib/export-time-entries'
import { generateTimeEntriesXLSX } from '@/lib/export-time-entries-xlsx'
import type { TimeEntriesExpanded } from '@/types/timeTracking'

interface UseExportXLSXOptions {
    periodLabel: string
    entriesByUser: Record<string, TimeEntriesExpanded[]>
    resolveTagNames?: TagNameLookup
}

export function useExportXLSX({
    periodLabel,
    entriesByUser,
    resolveTagNames,
}: UseExportXLSXOptions) {
    const [isExporting, setIsExporting] = useState(false)

    const exportToXLSX = async () => {
        setIsExporting(true)
        try {
            const allEntries = Object.values(entriesByUser).flat()
            if (allEntries.length === 0) {
                return
            }

            const data = generateTimeEntriesXLSX(allEntries, {
                entriesByUser,
                resolveTagNames,
            })
            const sanitizedLabel = periodLabel.replace(/[^a-zA-Z0-9-]/g, '-')
            const filename = `time-entries-${sanitizedLabel}.xlsx`
            await downloadXLSX(data, filename)
        } catch (error) {
            captureException('Export XLSX failed', error)
        } finally {
            setIsExporting(false)
        }
    }

    return { exportToXLSX, isExporting }
}
